import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Spinner } from 'react-bootstrap';
import { FaCheck, FaTimes, FaRocket, FaWrench } from 'react-icons/fa';

import axios from 'axios';

import { Formik } from 'formik';
import * as Yup from 'yup';
import 'bootstrap/dist/css/bootstrap.min.css';

const API_DEMO_URL =
  'https://www.silamoney.com/live-demo?utm_source=console_promo&utm_medium=banner_graphic';
const CONSOLEURL = 'https://console.silamoney.com/register';
const SALESCALENDY = 'https://calendly.com/elena-rosca/live-demo';
const SILAMONEYCLICKS = 'silamoneyACHNowClicks';
const BASEAPIURL = process.env.REACT_APP_BASE_URL;
const CLICKEXPIRATION = 24 * 60 * 60 * 1000;
const ALLOWEDCLICKCOUNT = 3;

function App() {
  const [results, setResults] = useState(null);
  const [clickCount, setClickCount] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [prevRoutingNumbers, setPrevRoutingNumbers] = useState({});
  const [error, setError] = useState('');
  const checkIcon = <FaCheck style={{ color: 'green' }} />;
  const timesIcon = <FaTimes style={{ color: 'red' }} />;

  const setClicks = (clicks) => {
    return localStorage.setItem(SILAMONEYCLICKS, JSON.stringify(clicks));
  };
  // comment in the code below while developing to reset the click limiter.
  // setClicks(null);

  const getClicks = () => {
    return JSON.parse(localStorage.getItem(SILAMONEYCLICKS)) || [];
  };

  useEffect(() => {
    const savedClicks = getClicks();
    const now = new Date().getTime();
    const validClicks = savedClicks.filter(
      (click) => now - click < CLICKEXPIRATION,
    );

    setClickCount(validClicks.length);
    if (validClicks.length > 3) {
      setDisabled(true);
    }
    setClicks(validClicks);
  }, [results]);

  const validationSchema = Yup.object().shape({
    routingNumber: Yup.string()
      .matches(/^\d+$/, 'Routing number must contain only digits')
      .length(9, 'Routing number must be exactly 9 digits')
      .required('Routing number is required'),
  });

  const handleClick = () => {
    const now = new Date().getTime();
    const savedClicks = getClicks();
    const newClicks = [...savedClicks, now];
    const validClicks = newClicks.filter(
      (click) => now - click < CLICKEXPIRATION,
    );
    setClickCount(validClicks.length);
    setClicks(validClicks);

    if (validClicks.length >= ALLOWEDCLICKCOUNT) {
      setDisabled(true);
    }
  };

  const cacheRoutingNumber = (routingNumber, data) => {
    if (!(routingNumber in prevRoutingNumbers)) {
      const routingNumberClone = structuredClone(prevRoutingNumbers);
      routingNumberClone[routingNumber] = data;
      setPrevRoutingNumbers(routingNumberClone);
    }
  };

  const handleCheck = async (routingNumber) => {
    setError('');
    if (routingNumber in prevRoutingNumbers) {
      setResults(prevRoutingNumbers[routingNumber]);
    } else {
      try {
        const response = await axios.post(
          `${BASEAPIURL}api/check-routing-number`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
            routing_number: routingNumber,
          },
        );

        if (response.status === 200) {
          setResults(response.data);
          cacheRoutingNumber(routingNumber, response.data);
          handleClick();
        }
      } catch (returnedError) {
        let message =
          'Unable to verify the routing number at this time. Please check the routing number and try again.';
        if (returnedError.message) message = returnedError.message;
        setError(message);
      }
    }
  };

  return (
    <div className="app-container">
      <h1 className="header text-center">
        ACHNow Routing Number Instant Payment Checker
      </h1>
      {clickCount < ALLOWEDCLICKCOUNT ? (
        <Formik
          initialValues={{ routingNumber: '' }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            await handleCheck(values.routingNumber);
            setSubmitting(false);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit} className="mt-4">
              <div className="form-group">
                <label htmlFor="routingNumber" className="form-label">
                  {/* eslint-disable-line jsx-a11y/label-has-associated-control */}
                  Enter a nine-digit ABA Routing Number
                </label>
                <Form.Control
                  type="text"
                  name="routingNumber"
                  placeholder="Ex: 001234567"
                  onChange={(e) => {
                    setError('');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  value={values.routingNumber}
                  isInvalid={touched.routingNumber && !!errors.routingNumber}
                />
                <div className="text-danger" style={{ height: '24px' }}>
                  {touched.routingNumber && errors.routingNumber
                    ? errors.routingNumber
                    : error}
                </div>
              </div>
              <Button
                type="submit"
                disabled={disabled || isSubmitting}
                className="button"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" variant="light" className="mx-3" />
                    <span className="ml-3">Loading...</span>
                  </>
                ) : (
                  'GO!'
                )}
              </Button>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="text-danger mt-4 px-5">
          You have reached your query limit for the day. Need to check more
          routing numbers? We can analyze your Nacha file to determine if the
          transactions would settle instantly on RTP or FedNow.{' '}
          <a
            href={SALESCALENDY}
            target="_blank"
            className="contact-link"
            rel="noreferrer"
          >
            Contact Sales
          </a>{' '}
          for more info!
        </div>
      )}
      {results && (
        <Card className="card mt-4">
          <Card.Body>
            <Card.Title className="card-title">
              Capability Results for Routing Number{' '}
              {results.capabilities.aba_routing_number}
            </Card.Title>
            <Card.Text className="card-text">
              {results.capabilities.bank_name}
            </Card.Text>
            <div className="my-4">
              <div className="table-header">RTP Capable</div>
              <div className="table-row">
                <span className="icon">
                  {results.capabilities.rtp.credit_enabled === true
                    ? checkIcon
                    : timesIcon}
                </span>
                <span>Receive a payment (RTP)</span>
              </div>
              <div className="table-row">
                <span className="icon">
                  {results.capabilities.rtp.debit_enabled === true
                    ? checkIcon
                    : timesIcon}
                </span>
                <span>Receive an RfP (RTP)</span>
              </div>
              <div>
                <div className="table-header">FedNow Capable</div>
                <div className="table-row">
                  <span className="icon">
                    {results.capabilities.fednow.credit_enabled === true
                      ? checkIcon
                      : timesIcon}
                  </span>
                  <span>Receive a payment</span>
                </div>
                <div className="table-row">
                  <span className="icon">
                    {results.capabilities.fednow.debit_enabled === true
                      ? checkIcon
                      : timesIcon}
                  </span>
                  <span>Receive an RfP</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
      <div className="d-flex">
        <Card className="marketing-card me-4 mt-4">
          <Card.Body className="d-flex flex-column justify-content-between align-items-center">
            <div className="marketing-row">
              <FaRocket className="big-icon" />
              <Card.Title className="card-title">
                Schedule an ACHNow demo today
              </Card.Title>
            </div>
            <Button
              className="button"
              as="a"
              href={API_DEMO_URL}
              target="_blank"
              rel="noreferrer"
            >
              Save My Spot
            </Button>
          </Card.Body>
        </Card>
        <Card className="marketing-card marketing-card-col mt-4">
          <Card.Body className="d-flex flex-column justify-content-between align-items-center">
            <div className="marketing-column">
              <FaWrench className="big-icon" />
              <Card.Title className="card-title">
                Checkout our Sandbox
              </Card.Title>
            </div>
            <Button
              className="button"
              as="a"
              href={CONSOLEURL}
              target="_blank"
              rel="noreferrer"
            >
              Start Building
            </Button>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}

export default App;
